import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Partner, PartnerCustomModelSummary } from '../../api.js';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type { Tenant, TenantAssignment, ToastMessage } from '../../types.js';
import { PartnerCustomModelDeployTab } from '../partners/PartnerCustomModelDeployTab.js';

export function CustomModelDeploymentSection({
  http,
  tenants,
  partnerId,
  setPartnerId,
  busy,
  setBusy,
  onToast,
  onOpenAssignment,
  onChanged,
}: {
  http: AdminHttpClient;
  tenants: Tenant[];
  partnerId: string;
  setPartnerId: (partnerId: string) => void;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onToast: (toast: ToastMessage) => void;
  onOpenAssignment: () => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [customModels, setCustomModels] = useState<PartnerCustomModelSummary[]>([]);
  const [deployTenantId, setDeployTenantId] = useState<string>();
  const [deployAssignment, setDeployAssignment] = useState<TenantAssignment>();
  const customModelRequestRef = useRef(0);
  const deployRequestRef = useRef(0);
  const translateError = useCallback(
    (key: string, options?: Record<string, unknown>) =>
      options === undefined ? t(key) : t(key, options),
    [t]
  );
  const selectedPartner = partners.find((partner) => partner.id === partnerId);
  const ownedTenants = useMemo(
    () => tenants.filter((tenant) => tenant.partnerId === partnerId),
    [partnerId, tenants]
  );

  const fail = useCallback(
    (error: unknown) => onToast({ tone: 'error', message: describeError(error, translateError) }),
    [onToast, translateError]
  );

  useEffect(() => {
    void (async () => {
      try {
        const next = await http.listPartners();
        setPartners(next);
        if (partnerId && !next.some((partner) => partner.id === partnerId)) setPartnerId('');
      } catch (error) {
        fail(error);
      }
    })();
  }, [fail, http, partnerId, setPartnerId]);

  useEffect(() => {
    const requestId = ++customModelRequestRef.current;
    setDeployTenantId(undefined);
    setDeployAssignment(undefined);
    if (!partnerId) {
      setCustomModels([]);
      return;
    }
    void (async () => {
      try {
        const next = await http.listPartnerCustomModels(partnerId);
        if (customModelRequestRef.current === requestId) setCustomModels(next);
      } catch (error) {
        if (customModelRequestRef.current === requestId) fail(error);
      }
    })();
  }, [fail, http, partnerId]);

  const loadDeployAssignment = useCallback(async () => {
    const requestId = ++deployRequestRef.current;
    if (!deployTenantId) {
      setDeployAssignment(undefined);
      return;
    }
    try {
      const assignment = await http.getTenantAssignment(deployTenantId);
      if (deployRequestRef.current === requestId) setDeployAssignment(assignment);
    } catch (error) {
      if (deployRequestRef.current === requestId) {
        setDeployAssignment(undefined);
        fail(error);
      }
    }
  }, [deployTenantId, fail, http]);

  useEffect(() => {
    void loadDeployAssignment();
  }, [loadDeployAssignment]);

  async function deploy(modelKey: string) {
    if (!deployTenantId) return;
    setBusy(true);
    try {
      await http.platformDeployCustomModel(deployTenantId, modelKey);
      await Promise.all([loadDeployAssignment(), onChanged()]);
      onToast({ tone: 'success', message: t('platform.customModelDeployed') });
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(modelKey: string) {
    if (!deployTenantId) return;
    setBusy(true);
    try {
      await http.platformRevokeCustomModel(deployTenantId, modelKey);
      await Promise.all([loadDeployAssignment(), onChanged()]);
      onToast({ tone: 'success', message: t('platform.customModelRevoked') });
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="content-stack custom-model-deployment">
      <div className="panel section-heading">
        <div>
          <h2>{t('platform.customModelDeployment')}</h2>
          <span className="badge">{t('platform.definitionKindDedicated')}</span>
        </div>
        <div className="toolbar-form">
          <label>
            {t('platform.customModelPartner')}
            <select
              value={partnerId}
              disabled={busy}
              onChange={(event) => setPartnerId(event.target.value)}
            >
              <option value="">{t('platform.selectCustomModelPartner')}</option>
              {partners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name} ({partner.id})
                </option>
              ))}
            </select>
          </label>
          <button type="button" className="secondary" onClick={onOpenAssignment}>
            {t('manualProviderModels.openAssignment')}
          </button>
        </div>
      </div>
      {selectedPartner ? (
        <PartnerCustomModelDeployTab
          customModels={customModels}
          ownedTenants={ownedTenants}
          {...(deployTenantId === undefined ? {} : { deployTenantId })}
          {...(deployAssignment === undefined ? {} : { deployAssignment })}
          busy={busy}
          onTenantChange={(tenantId) => {
            setDeployAssignment(undefined);
            setDeployTenantId(tenantId);
          }}
          onDeploy={(modelKey) => void deploy(modelKey)}
          onRevoke={(modelKey) => void revoke(modelKey)}
        />
      ) : (
        <p className="panel muted">{t('platform.selectCustomModelPartner')}</p>
      )}
    </section>
  );
}
